import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useConsent } from '../consent/useConsent';
import { GA_MEASUREMENT_ID, analyticsConfigured } from '../data/analytics';
import { useLanguage } from '../i18n/useLanguage';
import { loadGoogleAnalytics, revokeGoogleAnalytics, trackEvent, trackPageView } from './googleAnalytics';

const LINK_TEXT_LIMIT = 80;

function describeLink(anchor: HTMLAnchorElement): Record<string, string | boolean> {
    const target = new URL(anchor.href, window.location.origin);
    const landmark = anchor.closest<HTMLElement>('section[id], footer, nav, header');
    return {
        link_text: (anchor.textContent ?? '').trim().slice(0, LINK_TEXT_LIMIT),
        link_url: target.href,
        outbound: target.origin !== window.location.origin,
        section: landmark?.id || landmark?.tagName.toLowerCase() || 'page'
    };
}

export function useAnalytics(): void {
    const { ready, decision } = useConsent();
    const { pathname, search } = useLocation();
    const { language } = useLanguage();
    const active = analyticsConfigured && ready && decision?.analytics === true;
    const wasActive = useRef(false);

    useEffect(() => {
        if (active) {
            loadGoogleAnalytics(GA_MEASUREMENT_ID);
            wasActive.current = true;
            return;
        }
        if (wasActive.current) {
            revokeGoogleAnalytics();
            wasActive.current = false;
        }
    }, [active]);

    useEffect(() => {
        if (!active) return;
        trackPageView(`${pathname}${search}`, document.title, language);
    }, [active, pathname, search, language]);

    useEffect(() => {
        if (!active) return;
        const onClick = (event: MouseEvent) => {
            const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
            if (!anchor) return;
            trackEvent('link_click', describeLink(anchor));
        };
        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
    }, [active]);
}
